import React, { useContext, createContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAPI, useRawAPI } from 'hooks/useApi';
import base64 from 'base-64';
import { SignInResult, AccessRequestResult, AccessCheckCodeResult, AccessPasswordResult } from 'types/auth';
import { OnboardingStatus } from 'constant';
import { OnboardingT } from 'types/onboarding';
import { CountryType } from 'types';
import { TermsAndPolicies } from 'types/auth';
import ENV from 'environment';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import type { EligibilityResponseData } from 'types/withdrawals';
import { setSentryUser } from 'sentry';

export const AUTH_BEARER_KEY = '_cp_token';
export const LOGOUT_EVT_KEY = '_ifcp_logout';

// ==============================|| AUTH HOOKS ||============================== //

type AuthContextType = {
    user?: User;
    terms?: TermsAndPolicies[];
    isReady: boolean;
    isOnboarded: boolean; // Current onboarding_status
    hasOnboarded: boolean; // Has being onboarded at least once
    signin?: Function;
    signout?: Function;
    resetPasswordRequest?: Function;
    resetCodeCheck?: Function;
    resetPassword?: Function;
    checkSession?: Function;
    acceptTerms?: (_id: string) => void;
    profilePicture?: string;
    loadingCurrentUser?: boolean;
    isEligibleForKYC?: boolean;
    kycLoaded?: boolean;
    hasOnDemand?: boolean;
    hasContractSigned?: boolean;
    bestDay40?: number;
    bestDay40Pctn?: number;
    kycLoadingError?: string;
    reloadKycEligibility?: () => Promise<boolean>;
    applyKycFallbackFromEligibility?: (data: EligibilityResponseData) => void;
};
const AuthContext = createContext<AuthContextType>({ isReady: false, isOnboarded: false, hasOnboarded: false });

export const AuthProvider = function AuthProvider({ children }: { children: string | JSX.Element | JSX.Element[] }) {
    const auth = useAuthProvider();
    // @ts-ignore
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = function useAuth(): AuthContextType {
    return useContext(AuthContext);
};
export default useAuth;

function useAuthProvider() {
    const [user, setUser] = useState<User | null>(null);
    const kycEligibilityHook = useKYCEligibility(user);
    const [isReady, setIsReady] = useState<boolean>(false);
    const [terms, setTerms] = useState<TermsAndPolicies[]>([]);
    const [loadingCurrentUser, setLoadingCurrentUser] = useState<boolean>(false);
    const navigate = useNavigate();
    let api = useRawAPI();

    const handleSuccessLogin = (u: User) => {
        if (u?.bearer?.length) {
            window.sessionStorage.setItem(AUTH_BEARER_KEY, u?.bearer);
            // eslint-disable-next-line react-hooks/rules-of-hooks
            api = useRawAPI(); //Refreshing the api to set bearer after login
        }
    };

    const handleUserSignout = () => {
        window.sessionStorage.clear();
        window.localStorage.setItem(LOGOUT_EVT_KEY, Date.now().toString());
        setUser(null);
        posthog.reset();
        setIsReady(true);
    };

    const signin = (email: string, password: string, recaptchaToken: string, token?: string) => {
        const resp = new Promise((resolve, reject) => {
            let data: any = { email, password, recaptcha_token: recaptchaToken };
            if (token) {
                data = { token };
            }
            api.post('/clients-dashboard/login', data)
                .then((result: SignInResult) => {
                    if (result && result.data) {
                        const resultData = result.data;
                        if (resultData.status == 'error' && resultData.error) {
                            reject(new Error(resultData.error[0]));
                        } else if (resultData.status === 'ok' && resultData.user) {
                            resolve(resultData.user);
                            /*
                            if(!resultData.user.kyc_status && resultData.user.documents_status){
                                resultData.user.kyc_status = resultData.user.documents_status;
                            }
                            */
                            let dataUser = resultData.user;
                            posthog.identify(dataUser.emails?.[0], {
                                email: dataUser.emails?.[0],
                                name: dataUser.display_name
                            });
                            dataUser.bearer = resultData.bearer ?? null;
                            handleSuccessLogin(dataUser);
                        }
                    }
                })
                .catch((error: Error) => {
                    reject(error);
                });
        });
        return resp;
    };

    const signout = () => {
        const resp = new Promise((resolve, reject) => {
            api.post('/users/logout', {})
                .then(() => {
                    handleUserSignout();
                })
                .catch((error: Error) => {
                    reject(error);
                });
        });
        return resp;
    };

    const resetPasswordRequest = (email: string, recaptchaToken: string) => {
        let resp = new Promise((resolve, reject) => {
            api.post('/clients-dashboard/reset-password/request', {
                email,
                recaptcha_token: recaptchaToken
            })
                .then((response: AccessRequestResult) => {
                    if (response.data) {
                        if (response.data.status == 'ok' && response.data.msg) {
                            resolve(response.data.msg[0]);
                        } else if (response.data.status == 'error' && response.data.errors) {
                            reject(new Error(response.data.errors[0]));
                        } else if (response.data.status == 'error' && response.data?.error) {
                            // Handle 'recaptcha_error' field in response
                            reject(new Error(response.data?.error[0]));
                        }
                    }
                })
                .catch(reject);
        });
        return resp;
    };

    const resetCodeCheck = (code: string) => {
        let resp = new Promise((resolve, reject) => {
            api.post('/clients-dashboard/reset-password/check-code', {
                code
            })
                .then((response: AccessCheckCodeResult) => {
                    if (response.data) {
                        if (response.data.status == 'ok' && response.data.user) {
                            resolve(response.data.user);
                        } else if (response.data.status == 'error' && response.data.errors) {
                            reject(new Error(response.data.errors[0]));
                        }
                    }
                })
                .catch(reject);
        });
        return resp;
    };

    const resetPassword = (code: string, _id: string, name: string, password: string, password_confirmation: string) => {
        let resp = new Promise((resolve, reject) => {
            api.post('/clients-dashboard/reset-password/process', {
                code,
                _id,
                name,
                password,
                password_confirmation,
                process_reset: true
            })
                .then((response: AccessPasswordResult) => {
                    if (response.data) {
                        if (response.data.status == 'ok' && response.data.user) {
                            resolve(response.data.user);
                        } else if (response.data.status == 'error' && response.data.errors) {
                            reject(new Error(response.data.errors[0]));
                        }
                    }
                })
                .catch(reject);
        });
        return resp;
    };

    const getCurrentUser = () =>
        api.post('/hub', {
            query: `{
                    current_member{
                        code:customer_number
                        _id
                        onboarding_status
                        has_onboarded
                        onboarding {
                            type
                            naming
                            allow_multiple_onboardings
                        }
                        title
                        display_name
                        first_name
                        last_name
                        emails
                        phones
                        alias
                        addresses {
                            address_line_1
                            address_line_2
                            address_line_3
                            city
                            postcode
                            country { _id name alpha_2 alpha_3 }
                            state
                            primary
                            phone
                            display_address
                        }
                        accounts_live_count
                        accounts_demo_count
                        kyc_status:documents_status
                        kyc_category {
                            _id
                            name
                        }
                        profile_pic{
                            url
                        }
                        birth_date
                        type
                        has_funded_accounts
                        created_at
                    }
                }`
        });

    const checkSession = () => {
        getCurrentUser()
            .then((resp) => {
                if (resp.data && resp.data.data && resp.data.data.current_member) {
                    let data = resp.data.data;
                    setUser(data.current_member);
                    if (resp.data.data._TERMS) {
                        setTerms(resp.data.data._TERMS);
                    }
                } else {
                    setUser(null);
                    setTerms([]);
                }
                setIsReady(true);
            })
            .catch((err) => {
                setUser(null);
                setIsReady(true);
            });
    };

    const acceptTerms = (_id: string) => {
        setTerms(terms.filter((item) => item._id != _id));
    };

    let profilePicture = useMemo(() => {
        if (user && user.profile_pic) {
            let prefix = user.profile_pic.url.includes('private') ? '/cp' : '';
            return ENV.API_BASE_URL + prefix + user.profile_pic.url;
        }
        return null;
    }, [user, ENV.API_BASE_URL]);

    useEffect(() => {
        setSentryUser(user);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?._id]);

    useEffect(() => {
        setLoadingCurrentUser(true);
        getCurrentUser()
            .then((resp) => {
                if (resp.data && resp.data.data && resp.data.data.current_member) {
                    setUser(resp.data.data.current_member);
                    if (resp.data.data._TERMS) {
                        setTerms(resp.data.data._TERMS);
                    }
                }
                setIsReady(true);
            })
            .catch((err) => {
                setIsReady(true);
            })
            .finally(() => {
                setLoadingCurrentUser(false);
            });
        window.addEventListener('storage', (event) => {
            if (event.key === LOGOUT_EVT_KEY) {
                window.sessionStorage.removeItem(AUTH_BEARER_KEY);
                setSentryUser(null);
                navigate('/login');
            }
        });
        // Cleanup subscription on unmount
        return () => {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        user,
        isReady,
        isOnboarded: user?.onboarding_status === OnboardingStatus.Onboarded,
        hasOnboarded: user?.has_onboarded === true,
        signin,
        signout,
        resetPasswordRequest,
        resetCodeCheck,
        resetPassword,
        checkSession,
        acceptTerms,
        terms,
        profilePicture,
        loadingCurrentUser,
        isEligibleForKYC: kycEligibilityHook.isEligibleForKYC,
        kycLoaded: kycEligibilityHook.ready || false,
        hasOnDemand: kycEligibilityHook.hasOnDemand,
        bestDay40: kycEligibilityHook.bestDay40,
        bestDay40Pctn: kycEligibilityHook.bestDay40Pctn,
        hasContractSigned: kycEligibilityHook.hasContractSigned,
        kycLoadingError: kycEligibilityHook.kycLoadingError,
        reloadKycEligibility: kycEligibilityHook.load,
        applyKycFallbackFromEligibility: kycEligibilityHook.applyFallbackFromEligibility
    };
}
type KYCEligibilityHook = {
    isEligibleForKYC: boolean;
    hasContractSigned: boolean;
    hasOnDemand: boolean;
    kycEnabledForOnDemandType: boolean;
    loading: boolean;
    ready: boolean;
    load: () => Promise<boolean>;
    applyFallbackFromEligibility: (data: EligibilityResponseData) => void;
    bestDay40: number;
    bestDay40Pctn: number;
    kycLoadingError: string;
};
export function useKYCEligibility(user: User | null): KYCEligibilityHook {
    const api = useAPI('use-kyc-eligibility-hook');
    const loadInFlightRef = useRef<Promise<boolean> | null>(null);
    const [isEligibleForKYC, setIsEligibleForKYC] = useState<boolean>(false);
    const [hasContractSigned, setHasContractSigned] = useState<boolean>(false);
    const [hasOnDemand, setHasOnDemand] = useState<boolean>(false);
    const [kycEnabledForOnDemandType, setKycEnabledForOnDemandType] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [ready, setReady] = useState<boolean>(false);
    const [bestDay40, setBestDay40] = useState<number>(0);
    const [bestDay40Pctn, setBestDay40Pctn] = useState<number>(0);
    const [kycLoadingError, setKycLoadingError] = useState<string>('');

    const applyFallbackFromEligibility = useCallback((data: EligibilityResponseData) => {
        const checks = data.checks;
        const isEligibleForKYCFromAccountCheck = data.is_kyc_elegible ?? false;

        if (!!checks.kyc_approved || isEligibleForKYCFromAccountCheck) {
            setIsEligibleForKYC(true);
            setKycLoadingError('');
            setReady(true);
        }
    }, []);

    const load = useCallback((): Promise<boolean> => {
        if (loadInFlightRef.current) {
            return loadInFlightRef.current;
        }
        setLoading(true);
        const p = api
            .get('/cc/kyc/check-kyc-eligibility')
            .then((response) => {
                const responseData = response?.data;
                if (responseData.status == 'ok') {
                    setKycLoadingError('');
                    if (responseData.is_eligible_for_kyc) {
                        setIsEligibleForKYC(true);
                    }
                    const accountsChecks = responseData.checks_by_account ? Object.values(responseData.checks_by_account) : null;
                    if (accountsChecks) {
                        if (accountsChecks.filter((accountCheck: any) => accountCheck.contract_signed).length > 0) {
                            setHasContractSigned(true);
                        }
                        let checks = [];
                        if (
                            (checks = accountsChecks.filter(
                                (accountCheck: any) =>
                                    accountCheck.hasOwnProperty('best_day_40') ||
                                    accountCheck.hasOwnProperty('best_day_15') ||
                                    accountCheck.hasOwnProperty('best_day_20')
                            )).length > 0
                        ) {
                            setHasOnDemand(true);
                            if (
                                checks.filter((check: any) => check.hasOwnProperty('best_day_15') || check.hasOwnProperty('best_day_20'))
                                    .length > 0
                            ) {
                                setKycEnabledForOnDemandType(true);
                            }
                            setBestDay40(responseData.best_profit);
                            setBestDay40Pctn(responseData.best_profit_pctn);
                        }
                    }
                    return true;
                } else if (responseData?.status === 'error' || responseData?.code === 400) {
                    setKycLoadingError('kyc_section.kyc_loading_error');
                    return false;
                }
                return false;
            })
            .catch(() => {
                setKycLoadingError('kyc_section.kyc_loading_error');
                return false;
            })
            .finally(() => {
                setLoading(false);
                setReady(true);
                loadInFlightRef.current = null;
            });
        loadInFlightRef.current = p;
        return p;
    }, [api]);

    useEffect(() => {
        if (user) {
            void load();
        } else if (isEligibleForKYC) {
            setIsEligibleForKYC(false);
            setReady(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?._id]);

    return {
        isEligibleForKYC,
        hasContractSigned,
        hasOnDemand,
        kycEnabledForOnDemandType,
        loading,
        load,
        applyFallbackFromEligibility,
        ready,
        bestDay40: bestDay40,
        bestDay40Pctn: bestDay40Pctn,
        kycLoadingError
    };
}

type ProfilePicType = {
    _id?: string;
    url: string;
};

export type AddressesType = {
    address_line_1?: string;
    address_line_2?: string;
    address_line_3?: string;
    city?: string;
    state?: string;
    postcode?: string;
    primary?: boolean;
    country: CountryType;
    phone: string;
};

export type User = {
    code: number;
    _id: string;
    title?: string;
    display_name: string;
    first_name: string;
    last_name: string;
    emails: Array<string>;
    phones: string[];
    profile_pic?: ProfilePicType;
    addresses: AddressesType[];
    accounts_live_count: number;
    accounts_demo_count: number;
    kyc_status?: string;
    kyc_category?: {
        _id: string;
        name: string;
    };
    kyc_category_id?: string;
    bearer?: string | null | undefined;
    onboarding_status: string;
    has_onboarded: boolean;
    onboarding?: OnboardingT;
    birth_date?: string;
    type: string;
    has_funded_accounts?: boolean;
    alias?: string;
    /** Account creation time from API (string date); used for Intercom `created_at` (Unix seconds). */
    created_at?: string | null;
};
